/**
 * Applies public display pricing to FAQ question lists (PBI #1193).
 */

import { FaqQuestions } from '../constants/faqQuestions';
import {
  faqFreePlanAnswerFallback,
  faqMailedCardPricingAnswerFallback,
  faqMemberPricingAnswerFallback,
} from '../content/faqContent';
import type { PublicSubscriptionConfig } from '../types/publicSubscriptionConfig';
import {
  buildFaqFreePlanAnswer,
  buildFaqMailedCardPricingAnswer,
  buildFaqMemberPricingAnswer,
  buildFaqWhoCanOrderAnswer,
} from './marketingPricingCopy';

const FREE_PLAN_QUESTIONS = new Set<string>([
  FaqQuestions.IsReallyFree,
  FaqQuestions.FreeAppBirthday,
]);

const WHO_CAN_ORDER_QUESTIONS = new Set<string>([FaqQuestions.WhoCanOrder]);

const MEMBER_PRICING_QUESTIONS = new Set<string>([FaqQuestions.WhatIsMemberPricing]);

const MAILED_PRICING_QUESTIONS = new Set<string>([FaqQuestions.SubscriptionCoversMailedCards]);

/**
 * Resolves a single FAQ answer, swapping in priced copy when config is available.
 */
export function resolveFaqAnswer(
  question: string,
  fallbackAnswer: string,
  config: PublicSubscriptionConfig | null,
): string {
  if (FREE_PLAN_QUESTIONS.has(question)) {
    return config ? buildFaqFreePlanAnswer(config) : faqFreePlanAnswerFallback;
  }

  if (WHO_CAN_ORDER_QUESTIONS.has(question)) {
    return buildFaqWhoCanOrderAnswer();
  }

  if (MEMBER_PRICING_QUESTIONS.has(question)) {
    return config ? buildFaqMemberPricingAnswer(config) : faqMemberPricingAnswerFallback;
  }

  if (MAILED_PRICING_QUESTIONS.has(question)) {
    return config ? buildFaqMailedCardPricingAnswer(config) : faqMailedCardPricingAnswerFallback;
  }

  return fallbackAnswer;
}

/** One rendered FAQ entry (question + resolved answer). */
export interface FaqItem {
  q: string;
  a: string;
}

/**
 * Flattens grouped FAQ categories into one list with each question appearing once
 * (the same pricing Q&A is shown under two categories on /faq). Used so FAQPage
 * JSON-LD lists exactly the visible questions without duplicate `Question` nodes.
 */
export function flattenUniqueFaqItems(
  groups: ReadonlyArray<ReadonlyArray<{ q?: string; a?: string }>>,
): FaqItem[] {
  const seen = new Set<string>();
  const result: FaqItem[] = [];
  for (const group of groups) {
    for (const item of group) {
      const q = item.q ?? '';
      const a = item.a ?? '';
      if (!q || !a || seen.has(q)) {
        continue;
      }
      seen.add(q);
      result.push({ q, a });
    }
  }
  return result;
}

/**
 * Maps FAQ items `{ q, a }` or `{ question, answer }` with priced answers when config is set.
 * Accepts readonly `as const` content arrays (marketing pages).
 */
export function mapFaqItemsWithPricing(
  items: ReadonlyArray<{ q?: string; a?: string; question?: string; answer?: string }>,
  config: PublicSubscriptionConfig | null,
): Array<{ q?: string; a?: string; question?: string; answer?: string }> {
  return items.map((item) => {
    const question = item.q ?? item.question ?? '';
    if (item.q != null && item.a != null) {
      return { ...item, a: resolveFaqAnswer(question, item.a, config) };
    }

    if (item.question != null && item.answer != null) {
      return { ...item, answer: resolveFaqAnswer(question, item.answer, config) };
    }

    return { ...item };
  });
}
